<script lang="ts">
  import { addCredits } from '../map/credits';
  import { onMount, onDestroy } from 'svelte';
  import * as maplibregl from 'maplibre-gl';
  import { api } from '../../api/client';
  import type { MapInfo, RouteSegment } from '../../api/types';
  import { followTheme, northUpMap, styleFor } from '../map/theme';
  import { palette } from '../../utils/palette';
  import { t } from '../../i18n/index.ts';

  // RouteMap draws an activity's route: each stretch as a line and each gap
  // as a dashed straight line across it (spec 006 spec edge cases). With
  // ends, a green dot marks the start and, once finished, a checkered one the
  // finish (spec 006 FR-037). marker is the point the activity's charts
  // are scrubbed to (spec 006 FR-043).
  interface Props {
    segments: RouteSegment[];
    ends?: boolean;
    finished?: boolean;
    height?: string;
    marker?: { lat: number; lon: number } | null;
  }
  let { segments, ends = false, finished = false, height = 'h-72', marker = null }: Props = $props();

  let el: HTMLDivElement | undefined = $state();
  let map: maplibregl.Map | null = null;
  let stopTheme: (() => void) | null = null;
  let ready = false;
  let startMarker: maplibregl.Marker | null = null;
  let finishMarker: maplibregl.Marker | null = null;
  let scrubMarker: maplibregl.Marker | null = null;

  function dot(kind: 'start' | 'finish', label: string): HTMLElement {
    const d = document.createElement('div');
    d.className = `route-end route-${kind}`;
    d.title = label;
    return d;
  }

  function draw() {
    if (!map || !ready) return;
    const lines = segments.filter((s) => s.points.length > 1).map((s) => s.points.map((p) => [p.lon, p.lat]));
    const gaps: number[][][] = [];
    for (let i = 1; i < segments.length; i++) {
      const a = segments[i - 1].points, b = segments[i].points;
      if (segments[i].gap_before && a.length && b.length) gaps.push([[a[a.length - 1].lon, a[a.length - 1].lat], [b[0].lon, b[0].lat]]);
    }
    const fc = (paths: number[][][]): GeoJSON.FeatureCollection => ({
      type: 'FeatureCollection',
      features: paths.map((c) => ({ type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: c } })),
    });
    (map.getSource('route') as maplibregl.GeoJSONSource).setData(fc(lines));
    (map.getSource('gaps') as maplibregl.GeoJSONSource).setData(fc(gaps));
    const all = segments.flatMap((s) => s.points);
    startMarker?.remove();
    finishMarker?.remove();
    startMarker = finishMarker = null;
    if (ends && all.length > 0) {
      const first = all[0], last = all[all.length - 1];
      startMarker = new maplibregl.Marker({ element: dot('start', t('activitiesRouteStart')) }).setLngLat([first.lon, first.lat]).addTo(map);
      if (finished) finishMarker = new maplibregl.Marker({ element: dot('finish', t('activitiesRouteFinish')) }).setLngLat([last.lon, last.lat]).addTo(map);
    }
    if (all.length > 0) {
      const b = new maplibregl.LngLatBounds();
      for (const p of all) b.extend([p.lon, p.lat]);
      map.fitBounds(b, { padding: 40, maxZoom: 16, duration: 0 });
    }
  }

  $effect(() => {
    void segments;
    void ends;
    void finished;
    draw();
  });

  // The scrubbed point moves one marker rather than redrawing the route.
  $effect(() => {
    const m = marker;
    if (!map) return;
    if (!m) {
      scrubMarker?.remove();
      scrubMarker = null;
      return;
    }
    if (!scrubMarker) {
      const d = document.createElement('div');
      d.className = 'route-end route-scrub';
      scrubMarker = new maplibregl.Marker({ element: d });
    }
    scrubMarker.setLngLat([m.lon, m.lat]).addTo(map);
  });

  onMount(async () => {
    let info: MapInfo | null = null;
    try {
      info = await api.get<MapInfo>('/map');
    } catch {
      // A blank background still shows the route.
    }
    if (!el) return;
    map = northUpMap({
      container: el,
      style: styleFor(info),
      center: [-98.5, 39.8],
      zoom: 3,
      attributionControl: false,
    });
    addCredits(map, info, 'other');
    stopTheme = followTheme(map, info, () => {
      map!.addSource('route', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map!.addSource('gaps', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map!.addLayer({ id: 'gaps', type: 'line', source: 'gaps', paint: { 'line-color': palette('overlay2'), 'line-width': 2, 'line-dasharray': [2, 2] } });
      map!.addLayer({ id: 'route', type: 'line', source: 'route', paint: { 'line-color': palette('blue'), 'line-width': 4 } });
      ready = true;
      draw();
    });
  });

  onDestroy(() => {
    stopTheme?.();
    map?.remove();
  });
</script>

<div bind:this={el} class="w-full {height} rounded"></div>

<style>
  :global(.route-end) {
    width: 16px;
    height: 16px;
    border-radius: 9999px;
    border: 2px solid var(--theme-base);
    box-shadow: var(--theme-shadow-sm);
  }
  :global(.route-start) {
    background: var(--theme-green);
  }
  :global(.route-scrub) {
    width: 18px;
    height: 18px;
    background: var(--theme-base);
    border: 4px solid var(--theme-text);
  }
  :global(.route-finish) {
    background: repeating-conic-gradient(var(--theme-text) 0 25%, var(--theme-base) 0 50%) 0 0 / 6px 6px;
  }
</style>
